import type { MapEdge, MapNode, NavigationData, RouteResult, Shop, ShopMapPoint } from "../types";

export const DEFAULT_START_NODE_ID = "tower-two-entrance";

function edgeKey(from: string, to: string): string {
  return `${from}->${to}`;
}

function makeAdjacency(edges: MapEdge[]): Map<string, Array<{ to: string; weight: number }>> {
  const adjacency = new Map<string, Array<{ to: string; weight: number }>>();

  for (const edge of edges) {
    const fromList = adjacency.get(edge.fromNodeId) ?? [];
    fromList.push({ to: edge.toNodeId, weight: edge.distanceWeight });
    adjacency.set(edge.fromNodeId, fromList);

    const toList = adjacency.get(edge.toNodeId) ?? [];
    toList.push({ to: edge.fromNodeId, weight: edge.distanceWeight });
    adjacency.set(edge.toNodeId, toList);
  }

  return adjacency;
}

export function findShortestPath(
  nodes: MapNode[],
  edges: MapEdge[],
  startNodeId: string,
  endNodeId: string
): { nodeIds: string[]; distance: number } | null {
  const nodeIds = new Set(nodes.map((node) => node.id));
  if (!nodeIds.has(startNodeId) || !nodeIds.has(endNodeId)) return null;

  const adjacency = makeAdjacency(edges);
  const distances = new Map<string, number>();
  const previous = new Map<string, string | null>();
  const unvisited = new Set(nodeIds);

  nodeIds.forEach((id) => {
    distances.set(id, id === startNodeId ? 0 : Number.POSITIVE_INFINITY);
    previous.set(id, null);
  });

  while (unvisited.size > 0) {
    const current = Array.from(unvisited).sort(
      (a, b) => (distances.get(a) ?? Infinity) - (distances.get(b) ?? Infinity)
    )[0];

    if (!current || current === endNodeId) break;
    unvisited.delete(current);

    for (const neighbor of adjacency.get(current) ?? []) {
      if (!unvisited.has(neighbor.to)) continue;
      const nextDistance = (distances.get(current) ?? Infinity) + neighbor.weight;
      if (nextDistance < (distances.get(neighbor.to) ?? Infinity)) {
        distances.set(neighbor.to, nextDistance);
        previous.set(neighbor.to, current);
      }
    }
  }

  const distance = distances.get(endNodeId) ?? Infinity;
  if (!Number.isFinite(distance)) return null;

  const path: string[] = [];
  let cursor: string | null = endNodeId;
  while (cursor) {
    path.unshift(cursor);
    cursor = previous.get(cursor) ?? null;
  }

  return { nodeIds: path, distance };
}

export function getShopPoint(shop: Shop, navigationData: NavigationData): ShopMapPoint | null {
  return navigationData.shopPoints.find((point) => point.shopId === shop.id) ?? null;
}

export function buildRouteToShop(shop: Shop, navigationData: NavigationData): RouteResult {
  const point = getShopPoint(shop, navigationData);
  if (!point) {
    return {
      nodeIds: [],
      distance: 0,
      steps: [`Use ${shop.floor} ${shop.locationCode}. Map placement is not available yet.`],
      status: "map-point-unknown"
    };
  }

  const path = findShortestPath(
    navigationData.nodes,
    navigationData.edges,
    DEFAULT_START_NODE_ID,
    point.nearestNodeId
  );

  if (!path) {
    return {
      nodeIds: [],
      distance: 0,
      steps: [`Start at the LG Tower Two entrance, then use the listed ${shop.floor} lot ${shop.lot}.`],
      status: "navigation-data-missing"
    };
  }

  const nodesById = new Map(navigationData.nodes.map((node) => [node.id, node]));
  const steps = path.nodeIds.map((nodeId, index) => {
    const node = nodesById.get(nodeId);
    if (!node) return `Continue to route point ${index + 1}.`;
    if (index === 0) return `Start at ${node.label}.`;
    return `Continue to ${node.label}.`;
  });

  steps.push(`Look for ${shop.name} at ${shop.locationCode}.`);

  return {
    ...path,
    steps,
    status: point.dataQuality === "verified" ? "route-available" : "route-estimated"
  };
}

export function isRouteEdgeHighlighted(route: RouteResult, edge: MapEdge): boolean {
  const pairs = new Set<string>();
  for (let index = 0; index < route.nodeIds.length - 1; index += 1) {
    const from = route.nodeIds[index];
    const to = route.nodeIds[index + 1];
    pairs.add(edgeKey(from, to));
    pairs.add(edgeKey(to, from));
  }
  return pairs.has(edgeKey(edge.fromNodeId, edge.toNodeId));
}
